/**
 * Copy for the Laboratório's "E-mails que não viraram conta" decision cards (ALC-4471).
 * Namespace `noBillEmails`. Reason and refusal copy is keyed by the stable
 * codes from `@alinea/types` (both locales live there), never by message text.
 */
import {
  EMAIL_INTAKE_NO_BILL_ERROR_MESSAGES,
  EMAIL_INTAKE_REASON_COPY_EN,
  EMAIL_INTAKE_REASON_COPY_PT_BR,
  type EmailIntakeNoBillErrorCode,
  type EmailIntakeReasonCode,
} from '@alinea/types';

type ReasonCopy = Record<EmailIntakeReasonCode, { message: string; nextStep: string }>;

function reasons(catalog: Record<string, { finance: { message: string; nextStep: string } }>) {
  return Object.fromEntries(
    Object.entries(catalog).map(([code, copy]) => [
      code,
      { message: copy.finance.message, nextStep: copy.finance.nextStep },
    ]),
  ) as ReasonCopy;
}

function pick<K extends string>(
  catalog: Record<K, { ptBr: string; en: string }>,
  locale: 'ptBr' | 'en',
): Record<K, string> {
  return Object.fromEntries(
    Object.entries<{ ptBr: string; en: string }>(catalog).map(([code, copy]) => [
      code,
      copy[locale],
    ]),
  ) as Record<K, string>;
}

const ERRORS_PT_BR = pick<EmailIntakeNoBillErrorCode>(EMAIL_INTAKE_NO_BILL_ERROR_MESSAGES, 'ptBr');
const ERRORS_EN = pick<EmailIntakeNoBillErrorCode>(EMAIL_INTAKE_NO_BILL_ERROR_MESSAGES, 'en');

export const noBillEmailsPtBr = {
  row: {
    noSubject: '(sem assunto)',
    attachments_one: '{{count}} anexo',
    attachments_other: '{{count}} anexos',
    reasonUnknown: 'O e-mail não gerou conta.',
  },
  actions: {
    retry: 'Tentar novamente',
    dismiss: 'Dispensar',
  },
  reasons: reasons(EMAIL_INTAKE_REASON_COPY_PT_BR),
  errors: {
    ...ERRORS_PT_BR,
    generic: 'Algo deu errado. Tente de novo.',
  },
} as const;

export const noBillEmailsEn = {
  row: {
    noSubject: '(no subject)',
    attachments_one: '{{count}} attachment',
    attachments_other: '{{count}} attachments',
    reasonUnknown: 'The e-mail did not create a bill.',
  },
  actions: {
    retry: 'Try again',
    dismiss: 'Dismiss',
  },
  reasons: reasons(EMAIL_INTAKE_REASON_COPY_EN),
  errors: {
    ...ERRORS_EN,
    generic: 'Something went wrong. Try again.',
  },
} as const;
